import { useCallback } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import type { ProgressData } from '@/lib/curriculum/types';
import { DEFAULT_PROGRESS } from '@/lib/curriculum';

export function useProgress() {
  const [progress, setProgress] = useLocalStorage<ProgressData>(
    'reality-architect:progress',
    DEFAULT_PROGRESS
  );

  const markLessonComplete = useCallback(
    (lessonId: string) => {
      setProgress((prev) => ({
        ...prev,
        completedLessons: { ...prev.completedLessons, [lessonId]: true },
        lastActivityAt: new Date().toISOString(),
      }));
    },
    [setProgress]
  );

  const markLessonIncomplete = useCallback(
    (lessonId: string) => {
      setProgress((prev) => ({
        ...prev,
        completedLessons: { ...prev.completedLessons, [lessonId]: false },
        lastActivityAt: new Date().toISOString(),
      }));
    },
    [setProgress]
  );

  const toggleLessonComplete = useCallback(
    (lessonId: string) => {
      setProgress((prev) => ({
        ...prev,
        completedLessons: {
          ...prev.completedLessons,
          [lessonId]: !prev.completedLessons[lessonId],
        },
        lastActivityAt: new Date().toISOString(),
      }));
    },
    [setProgress]
  );

  const updateNote = useCallback(
    (lessonId: string, note: string) => {
      setProgress((prev) => ({
        ...prev,
        lessonNotes: { ...prev.lessonNotes, [lessonId]: note },
        lastActivityAt: new Date().toISOString(),
      }));
    },
    [setProgress]
  );

  const setCurrentPosition = useCallback(
    (moduleId: string, lessonId: string) => {
      setProgress((prev) => ({
        ...prev,
        currentModule: moduleId,
        currentLesson: lessonId,
        lastActivityAt: new Date().toISOString(),
      }));
    },
    [setProgress]
  );

  const resetProgress = useCallback(() => {
    setProgress(DEFAULT_PROGRESS);
  }, [setProgress]);

  const isLessonComplete = useCallback(
    (lessonId: string): boolean => {
      return !!progress.completedLessons[lessonId];
    },
    [progress.completedLessons]
  );

  return {
    progress,
    markLessonComplete,
    markLessonIncomplete,
    toggleLessonComplete,
    updateNote,
    setCurrentPosition,
    resetProgress,
    isLessonComplete,
  };
}
